import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';

/**
 * Injection token for auditing API path
 */
export const AUDITING_API_PATH = new InjectionToken<string>('AUDITING_API_PATH');

export interface ActivityResponse {
  [key: string]: any;
}

/**
 * Activities API Service for handling activity-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class ActivitiesApiService {
  constructor(
    private networkService: NetworkService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(AUDITING_API_PATH) private auditingApiPath: string,
  ) {}

  /**
   * Get user activities from the control plane API
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param entityTypes Array of entity types to filter by (e.g., ['DATABANK', 'AIMODEL'])
   * @param operations Array of operations/activities to filter by (e.g., ['CREATE', 'UPDATE'])
   * @param startDate Start date for date range filter (ISO format: YYYY-MM-DDTHH:mm:ss)
   * @param endDate End date for date range filter (ISO format: YYYY-MM-DDTHH:mm:ss)
   * @param sort Sort string (e.g., 'fieldName:asc' or 'fieldName:desc')
   * @param logType Optional log type filter: ASSET | COMPUTE | USER_ACTION (if backend supports it)
   * @returns Promise with activities data
   */
  async getUserActivities(
    page: number = 1, 
    size: number = 10, 
    entityTypes?: string[], 
    operations?: string[],
    startDate?: string,
    endDate?: string,
    sort?: string,
    logType?: string
  ): Promise<ActivityResponse> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());

      // Add logType query parameter if provided (ASSET | COMPUTE | USER_ACTION)
      if (logType && logType.trim()) {
        params.append('logType', logType.trim());
      }

      // Add sort parameter if provided (format: fieldName:asc or fieldName:desc)
      if (sort && sort.trim()) {
        params.append('sort', sort.trim());
      }
      
      // Add multiple assetType query parameters if provided
      if (entityTypes && entityTypes.length > 0) {
        entityTypes.forEach(assetType => {
          params.append('assetType', assetType);
        });
      }

      // Add multiple action query parameters if provided (Activity filter)
      if (operations && operations.length > 0) {
        operations.forEach(operation => {
          params.append('action', operation);
        });
      }

      // Add date range parameters if both start and end dates are provided
      if (startDate && endDate) {
        params.append('time', startDate);
        params.append('endtime', endDate);
        params.append('timerel', 'between');
      }

      // Construct the full URL with query parameters
      const url = `${this.basePathUrl}${this.auditingApiPath}consumer/activity?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const activitiesData = await this.networkService.get_api(
        url,
        undefined,
        false,
      );

      return activitiesData;
    } catch (error) {
      console.error('Error fetching user activities:', error);
      throw error;
    }
  }

  /**
   * Get all platform user activities from the control plane API
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @param assetTypes Array of asset types to filter by (e.g., ['adex:DataBank', 'adex:AiModel'])
   * @param operations Array of operations/activities to filter by (e.g., ['Approve', 'Create', 'Reject'])
   * @param startDate Start date for date range filter (ISO format: YYYY-MM-DDTHH:mm:ss)
   * @param endDate End date for date range filter (ISO format: YYYY-MM-DDTHH:mm:ss)
   * @param sort Sort string (e.g., 'action:asc' or 'createdAt:desc')
   * @param logType Log type filter: ASSET | COMPUTE | USER_ACTION
   * @returns Promise with activities data
   */
  async getAllPlatformUserActivities(page: number = 1, size: number = 10, assetTypes?: string[], operations?: string[], startDate?: string, endDate?: string, sort?: string, logType?: string): Promise<ActivityResponse> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());

      // Add logType query parameter if provided (ASSET | COMPUTE | USER_ACTION)
      if (logType && logType.trim()) {
        params.append('logType', logType.trim());
      }

      // Add sort parameter if provided (format: fieldName:asc or fieldName:desc)
      if (sort && sort.trim()) {
        params.append('sort', sort.trim());
      }
      
      // Add multiple assetType query parameters if provided
      if (assetTypes && assetTypes.length > 0) {
        assetTypes.forEach(assetType => {
          params.append('assetType', assetType);
        });
      }

      // Add multiple action query parameters if provided (Activity filter)
      if (operations && operations.length > 0) {
        operations.forEach(operation => {
          params.append('action', operation);
        });
      }

      // Add date range parameters if both start and end dates are provided
      if (startDate && endDate) {
        params.append('time', startDate);
        params.append('endtime', endDate);
        params.append('timerel', 'between');
      }

      // Construct the full URL with query parameters
      const url = `${this.basePathUrl}${this.auditingApiPath}admin/activity?${params.toString()}`;

      // Make the API call using NetworkService (headers are added automatically by interceptor)
      const activitiesData = await this.networkService.get_api(
        url,
        undefined,
        false, // Toaster - set to true if you don't want error toasters
      );

      return activitiesData;
    } catch (error) {
      console.error('Error fetching all platform user activities:', error);
      throw error;
    }
  }

  /**
   * Admin activity export report (CSV or JSON) — GET `.../auditing/reportactivity/admin?logType=...`
   * Returns the raw response body as a Blob so callers can treat it as CSV text or parse JSON.
   */
  async getAdminActivityReport(logType: string): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('logType', logType.trim());
    const url = `${this.basePathUrl}${this.auditingApiPath}reportactivity/admin?${params.toString()}`;
    return this.networkService.get_api(url, { responseType: 'blob' }, false);
  }

  /**
   * Consumer activity export report (CSV) — GET `.../auditing/reportactivity/consumer`
   * Same endpoint used by tgdex for consumer / provider / org-admin / platform-admin My Activity.
   */
  async getConsumerActivityReport(): Promise<Blob> {
    const url = `${this.basePathUrl}${this.auditingApiPath}reportactivity/consumer`;
    return this.networkService.get_api(url, { responseType: 'blob' }, false);
  }
}

